import {
    Button, Col, Form, Input, Modal, notification, Row, Select, Space, Switch, Typography,
} from 'antd';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { closeModal } from '../../../actions/modals';
import { ModalTypes } from '../../../types/modals';
import { updateCoverage } from '../../../api/deliveryProviders';
import { Coverage } from '../../../const/coverages';
import { PAYMENT_METHODS } from '../../../const/paymentMethods';
import { VEHICLE_TYPES } from '../../../const/vehicles';

const { Title, Text } = Typography;

interface ICoverageEditModal {
    onSuccess?: (coverage: Coverage) => void
    editPolygon?: (coverageId: string) => void
    deleteCoverage?: (coverageId: string) => void
}

const OPTIONS = [
    { value: PAYMENT_METHODS.CASH, label: 'Cash' },
    { value: PAYMENT_METHODS.CARD, label: 'Card' },
    { value: PAYMENT_METHODS.BILLING_CARD, label: 'Billing Card' },
    { value: PAYMENT_METHODS.BILLING_TRANSFER, label: 'Billing Transfer' },
    { value: PAYMENT_METHODS.TERMINAL, label: 'Terminal' },
];

const VEHICLE_OPTIONS = [
    { value: VEHICLE_TYPES.LITE, label: 'Car' },
    { value: VEHICLE_TYPES.BIKE, label: 'Bike' },
];

const CoverageEditModal = ({
    onSuccess,
    editPolygon,
    deleteCoverage,
}: ICoverageEditModal) => {
    const dispatch = useDispatch();
    const [form] = Form.useForm();

    const [loading, setLoading] = useState(false);

    const { activeModal, modalData } = useSelector((state: any) => state.modals);
    const isOpen = activeModal === ModalTypes.CoverageEdit;

    const handleCancel = () => {
        dispatch(closeModal());
    };

    const onSubmit = async (values: any) => {
        const { _id, ...payload } = values;
        setLoading(true);
        try {
            const { data } = await updateCoverage(_id, payload);
            const coverage = data.data;

            notification.success({
                message: 'Region Edited',
                description: 'Region edited successfully',
            });

            onSuccess && onSuccess(coverage);
            handleCancel();
        } catch (error: any) {
            const { message } = error.response.data;
            notification.error({
                message: 'Error',
                description: message,
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        form.resetFields();
        form.setFieldsValue({ ...modalData?.coverage });
    }, [modalData]);

    const handleEditPolygon = () => {
        editPolygon && editPolygon(modalData?.coverage?._id);
        handleCancel();
    };

    const handleDelete = (coverageId: string) => {
        deleteCoverage && deleteCoverage(coverageId);
        handleCancel();
    };

    return (
        <Modal
            width="40%"
            visible={isOpen}
            title={(
                <Space align="center">
                    <Title style={{ margin: '0 8px 0 0' }} level={5}>
                        Coverage Detail
                        {' '}
                        {modalData?.coverage?.name}
                    </Title>
                </Space>
            )}
            footer={([
                <Button onClick={handleCancel}>
                    Cancel
                </Button>,
                <Button
                    type="primary"
                    onClick={() => form.submit()}
                    loading={loading}
                >
                    Save
                </Button>,
            ])}
            onCancel={handleCancel}
        >

            <Form
                disabled={loading}
                form={form}
                layout="vertical"
                onFinish={onSubmit}
                style={{ width: '100%' }}
                initialValues={{
                    carName: VEHICLE_TYPES.BIKE,
                }}
            >
                <Form.Item name="_id" hidden>
                    <Input />
                </Form.Item>
                <Form.Item name="name" label="Name">
                    <Input placeholder="Coverage Name" />
                </Form.Item>
                <Form.Item name="paymentMethod" label="Payment Method">
                    <Select
                        mode="multiple"
                        placeholder="Please select payment method"
                        options={OPTIONS}
                    />
                </Form.Item>
                <Form.Item name="carName" label="Vehicle Type">
                    <Select
                        mode="multiple"
                        defaultValue={VEHICLE_TYPES.BIKE}
                        options={VEHICLE_OPTIONS}
                        placeholder="Please select vehicle type"
                    />
                </Form.Item>
                <Form.Item name="isVisible" label="Visible" valuePropName="checked">
                    <Switch
                        checkedChildren="Yes"
                        unCheckedChildren="No"
                    />
                </Form.Item>
                <Form.Item>
                    <Row justify="start" align="middle" gutter={[16, 16]}>
                        <Col span={24}>
                            <Text>
                                You can edit the polygon by clicking or delete this coverage
                            </Text>
                        </Col>
                        <Col>
                            <Button
                                onClick={() => handleDelete(modalData?.coverage?._id)}
                                type="primary"
                                danger
                            >
                                Delete Polygon
                            </Button>
                        </Col>
                        <Col>
                            <Button
                                onClick={handleEditPolygon}
                                type="ghost"
                            >
                                Edit Polygon
                            </Button>
                        </Col>
                    </Row>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default CoverageEditModal;
